import React from 'react';
import { Button, Space } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { NumericInput } from './NumericInput';

type NumericValue = number | string | null;

type QuantityStepperInputProps = Omit<React.ComponentProps<typeof NumericInput>, 'onChange'> & {
  incrementAriaLabel: string;
  onChange?: (value: NumericValue) => void;
};

const toFiniteNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

export const QuantityStepperInput: React.FC<QuantityStepperInputProps> = ({
  value,
  onChange,
  incrementAriaLabel,
  addonAfter,
  style,
  min,
  max,
  step = 1,
  precision,
  disabled,
  size,
  ...props
}) => {
  const stepValue = toFiniteNumber(step) ?? 1;
  const minValue = toFiniteNumber(min);
  const maxValue = toFiniteNumber(max);
  const currentValue = toFiniteNumber(value);
  const baseValue = currentValue ?? (minValue == null ? 0 : minValue - stepValue);
  const isIncrementDisabled = disabled || (maxValue != null && baseValue >= maxValue);

  const handleIncrement = () => {
    let nextValue = baseValue + stepValue;

    if (minValue != null) {
      nextValue = Math.max(nextValue, minValue);
    }
    if (maxValue != null) {
      nextValue = Math.min(nextValue, maxValue);
    }
    if (typeof precision === 'number') {
      nextValue = Number(nextValue.toFixed(precision));
    }

    onChange?.(nextValue);
  };

  return (
    <Space.Compact style={style}>
      <NumericInput
        {...props}
        min={min}
        max={max}
        step={step}
        precision={precision}
        disabled={disabled}
        size={size}
        value={value}
        onChange={onChange}
        addonAfter={addonAfter}
        style={{ flex: 1, minWidth: 0 }}
      />
      <Button
        htmlType="button"
        disabled={isIncrementDisabled}
        aria-label={incrementAriaLabel}
        size={size}
        icon={<PlusOutlined aria-hidden="true" />}
        onClick={handleIncrement}
      />
    </Space.Compact>
  );
};

export default QuantityStepperInput;
